import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  Bell,
  Building2,
  CircleCheck,
  CircleUserRound,
  House,
  LogOut,
  LUCIDE_ICONS,
  LucideAngularModule,
  LucideIconProvider,
  PanelLeftClose,
  PanelLeftOpen,
  PieChart,
  SquarePlus,
} from 'lucide-angular';
import { RolPlataforma } from '../../core/models/novedades.models';
import { RoleContextService } from '../../core/services/role-context.service';
import { getMenuPorRol, MenuItem } from '../../core/utils/menu-por-rol.util';

@Component({
  selector: 'app-sidebar-menu',
  imports: [CommonModule, RouterLink, RouterLinkActive, LucideAngularModule],
  providers: [
    {
      provide: LUCIDE_ICONS,
      multi: true,
      useValue: new LucideIconProvider({
        Bell,
        House,
        CircleUserRound,
        CircleCheck,
        SquarePlus,
        Building2,
        PieChart,
        LogOut,
        PanelLeftClose,
        PanelLeftOpen,
      }),
    },
  ],
  templateUrl: './sidebar-menu.html',
  styleUrl: './sidebar-menu.css',
})
export class SidebarMenu {
  // Estado base del menú lateral: rol resuelto, opciones visibles y modal de cierre.
  private readonly menuColapsadoStorageKey = 'anid.sidebar.colapsado';
  rolActual: RolPlataforma = 'postulante';
  menu: MenuItem[] = getMenuPorRol(this.rolActual);
  menuColapsado = this.leerEstadoMenuColapsado();
  modalCerrarSesionVisible = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly roleContext: RoleContextService
  ) {
    this.route.queryParamMap.subscribe((params) => {
      // Cada cambio de rol recalcula menú dinámico sin recargar la aplicación.
      this.rolActual = this.roleContext.resolveRole(params, this.rolActual);
      this.menu = getMenuPorRol(this.rolActual);
    });
  }

  alternarMenu(): void {
    this.menuColapsado = !this.menuColapsado;
    this.guardarEstadoMenuColapsado();
  }

  // Conserva el role en query params para mantener navegación contextual entre módulos mock.
  queryParamsFor(item: MenuItem): Record<string, string> | null {
    const params: Record<string, string> = {};
    if (item.includeRoleParam) params['role'] = this.rolActual;
    if (item.queryParams) Object.assign(params, item.queryParams);
    return Object.keys(params).length > 0 ? params : null;
  }

  // Abre confirmación antes de salir para evitar cierres accidentales.
  abrirModalCerrarSesion(item: MenuItem): void {
    if (item.action !== 'logout') return;
    this.modalCerrarSesionVisible = true;
  }

  cancelarCerrarSesion(): void {
    this.modalCerrarSesionVisible = false;
  }

  // Cierre definitivo: vuelve a la portada pública.
  confirmarCerrarSesion(): void {
    this.modalCerrarSesionVisible = false;
    this.router.navigate(['/inicio-publico'], { replaceUrl: true });
  }

  private leerEstadoMenuColapsado(): boolean {
    try {
      return localStorage.getItem(this.menuColapsadoStorageKey) === 'true';
    } catch {
      return false;
    }
  }

  private guardarEstadoMenuColapsado(): void {
    try {
      localStorage.setItem(this.menuColapsadoStorageKey, String(this.menuColapsado));
    } catch {
      // Si el navegador bloquea storage, el menú sigue funcionando en memoria.
    }
  }
}
